import { useEffect, useRef, useState } from 'react';
import { GazeData } from '../types';
import { EMA_ALPHA } from '../config/constants';
import { clamp } from '../engine/stats';
import type { Landmark } from '../engine/rppg/SkinRoi';
import { classifyGazeZone, ZONE_META, type GazeZone } from '../engine/GazeZoneClassifier';
import { detectHeadPose, getHeadPoseSync } from '../engine/HeadPoseOnnx';
import type { Detection } from './useObjectDetector';
import { applyHomography, classifyPaperRegion, classifyPaperZone, PAPER_REGION_LABELS, PAPER_ZONE_META, type Point2D as HPoint, type PaperZone, type PaperRegion } from '../engine/Homography';
import { PaperGazeFilter } from '../engine/PaperGazeFilter';
import {
  PAPER_ZONE_MARGIN,
  PAPER_OUTSIDE_WINDOW_MS,
  PAPER_OUTSIDE_THRESHOLD,
  HEADPOSE_RAY_ENABLED,
  HEADPOSE_RAY_SHOW_LEGACY_CONE,
  IS_RECORDING_MODE,
} from '../config/constants';
import { castHeadPoseRay, firstObjectAlongRay } from '../engine/HeadPoseRay';
import { detectMarkersPartial } from '../engine/MarkerDetector';
import { computePartialHomography, PAPER_DST } from '../engine/Homography';
import type { PoseHeadEstimate } from './usePoseLandmarker';
import type { PhoneGripResult } from '../engine/PhoneGripDetector';
import { classifyPartialVisibility, type PartialGazeEstimate } from '../engine/PartialVisibilityClassifier';

// Distilled ONNX head-pose override (off by default). Replaces MediaPipe's
// matrix-derived yaw/pitch with the 0.32MB student model when `?headPoseOnnx=on`.
// ?hpFlipYaw / ?hpFlipPitch align the model's 300W-LP sign to the live MediaPipe
// convention during on-device calibration.
const _hpSearch = typeof window !== 'undefined' ? window.location.search : '';
const HEADPOSE_ONNX = _hpSearch.includes('headPoseOnnx=on');
const HP_FLIP_YAW = _hpSearch.includes('hpFlipYaw');
const HP_FLIP_PITCH = _hpSearch.includes('hpFlipPitch');

export interface PoseAuxInfo extends PoseHeadEstimate {
  shoulderAngle: number;
  /** Raw 33-keypoint pose landmarks (with visibility). Used by
   *  PartialVisibilityClassifier when face landmark is lost. */
  rawLandmarks?: Array<{ x: number; y: number; z?: number; visibility?: number }> | null;
}

export interface PhoneGripAuxInfo {
  grip: PhoneGripResult;
  /** Consecutive frames to debounce jitter. */
  sustainFrames: number;
}

/**
 * Per-frame gaze-vs-paper classification snapshot. Written every detect tick
 * so external consumers (e.g. SynthTestApp dwell tracker) can sample the
 * filtered zone without re-running the homography logic.
 */
export interface PaperZoneState {
  zone: PaperZone;
  region: PaperRegion | null;
  /** Was the gaze target off-screen for this frame? */
  offScreen: boolean;
  /** performance.now ms for this snapshot. */
  t: number;
  /** How the active homography was estimated this frame.
   *  - 'full'        : 4-point DLT (all markers detected)
   *  - 'affine'      : 3-point affine fallback (one marker missing)
   *  - 'calibration' : reused frozen calibration H (markers lost >threshold)
   *  - 'none'        : no homography this frame
   */
  homographyKind?: 'full' | 'affine' | 'calibration' | 'none';
  /** Confidence proxy for homographyKind, in [0,1]. */
  homographyConfidence?: number;
}

/**
 * MediaPipe FaceLandmarker hook — provides head pose, face landmarks, and overlay drawing.
 * Primary face detection system. WebEyeTracker (if available) enhances with BlazeGaze.
 *
 * Also exposes the raw face landmarks via `landmarksRef` so downstream hooks
 * (e.g. useRppg) can sample skin ROIs without re-running the detector.
 */
export interface FaceBbox {
  minX: number; minY: number; maxX: number; maxY: number;
}

export function useFaceLandmarker(
  videoElementId: string,
  overlayCanvasId: string,
  detectionsRef?: React.MutableRefObject<Detection[]>,
  paperHomographyRef?: React.MutableRefObject<Float64Array | null>,
  paperCornersRef?: React.MutableRefObject<HPoint[]>,
  poseHeadRef?: React.MutableRefObject<PoseHeadEstimate>,
  calibHomographyRef?: React.MutableRefObject<Float64Array | null>,
  calibCornersRef?: React.MutableRefObject<HPoint[]>,
  poseAuxRef?: React.MutableRefObject<PoseAuxInfo | null>,
  phoneGripRef?: React.MutableRefObject<PhoneGripAuxInfo | null>,
  paperZoneStateRef?: React.MutableRefObject<PaperZoneState | null>,
  /** Live override for PAPER_ZONE_MARGIN. Falls back to constant if absent. */
  paperZoneMarginRef?: React.MutableRefObject<number>,
  /** Face bbox output ref — written every frame face is detected.
   *  Consumed by useHandLandmarker to reject hand-on-face false positives. */
  faceBboxRef?: React.MutableRefObject<FaceBbox | null>,
) {
  const [gazeData, setGazeData] = useState<GazeData | null>(null);
  const [hasFace, setHasFace] = useState(false);
  const [fps, setFps] = useState(0);
  const [gazeZone, setGazeZone] = useState<GazeZone>('forward');
  const [faceNormY, setFaceNormY] = useState<number | null>(null);

  const faceLandmarkerRef = useRef<any>(null);
  const drawUtilsRef = useRef<any>(null);
  const hasInitializedRef = useRef(false);
  const animRef = useRef(0);
  const smoothRef = useRef({ yaw: 0, pitch: 0, roll: 0 });
  const paperGazeFilterRef = useRef(new PaperGazeFilter(PAPER_OUTSIDE_WINDOW_MS, PAPER_OUTSIDE_THRESHOLD));
  const markerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hpCanvasRef = useRef<HTMLCanvasElement | null>(null);  // head-pose ONNX crop
  const markerFrameCountRef = useRef(0);
  const markerLostCountRef = useRef(0);
  const latestHomographyKindRef = useRef<'full' | 'affine' | 'calibration' | 'none'>('none');
  const latestHomographyConfRef = useRef(0);
  const gazeSmooth = useRef({ x: 0, y: 0 });
  const frameCountRef = useRef(0);
  const fpsTimerRef = useRef(0);
  // Raw face landmarks, updated every detection cycle. Consumers should read
  // this via .current and MUST NOT mutate the array.
  const landmarksRef = useRef<Landmark[] | null>(null);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    let disposed = false;

    const init = async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const { FaceLandmarker, FilesetResolver, DrawingUtils } = vision;

        const filesetResolver = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
        );

        const faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFacialTransformationMatrixes: true,
          outputFaceBlendshapes: false,
        });

        if (disposed) return;
        faceLandmarkerRef.current = faceLandmarker;

        const waitForVideo = () => {
          const videoEl = document.getElementById(videoElementId) as HTMLVideoElement;
          if (!videoEl || !videoEl.videoWidth || videoEl.readyState < 2) {
            if (!disposed) setTimeout(waitForVideo, 300);
            return;
          }
          startDetection(videoEl, DrawingUtils, FaceLandmarker);
        };
        waitForVideo();
      } catch (err) {
        console.error('[useFaceLandmarker] Init failed:', err);
      }
    };

    const startDetection = (videoEl: HTMLVideoElement, DrawingUtils: any, FaceLandmarker: any) => {
      const overlayCanvas = document.getElementById(overlayCanvasId) as HTMLCanvasElement;
      let overlayCtx: CanvasRenderingContext2D | null = null;

      if (overlayCanvas) {
        overlayCanvas.width = videoEl.videoWidth;
        overlayCanvas.height = videoEl.videoHeight;
        overlayCtx = overlayCanvas.getContext('2d');
        if (overlayCtx) {
          drawUtilsRef.current = new DrawingUtils(overlayCtx);
        }
      }

      let lastDetectTime = 0;

      const detect = () => {
        if (disposed) return;
        animRef.current = requestAnimationFrame(detect);

        const now = performance.now();
        if (now - lastDetectTime < 33) return;
        if (!videoEl.videoWidth || videoEl.readyState < 2) return;
        lastDetectTime = now;

        frameCountRef.current++;
        if (now - fpsTimerRef.current > 1000) {
          setFps(frameCountRef.current);
          frameCountRef.current = 0;
          fpsTimerRef.current = now;
        }

        try {
          const result = faceLandmarkerRef.current.detectForVideo(videoEl, now);

          if (overlayCtx && overlayCanvas) {
            overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
          }

          if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
            setHasFace(false);
            landmarksRef.current = null;
            // Clear face bbox so hand landmarker doesn't filter against
            // stale bbox after face is lost.
            if (faceBboxRef) faceBboxRef.current = null;

            // ── Face-lost fallback — 7-case partial visibility dispatcher ──
            // 박하연 연구원 피드백(2026-05) 반영: 단일 pose fallback cone 대신
            // 눈/어깨/코 가시성 조합으로 케이스 분기 + 신뢰도별 출력 차등.
            const pa = poseAuxRef?.current;
            if (overlayCtx && overlayCanvas && pa) {
              const cw = overlayCanvas.width;
              const ch = overlayCanvas.height;

              const estimate: PartialGazeEstimate = classifyPartialVisibility({
                poseLandmarks: pa.rawLandmarks ?? null,
                shoulderAngle: pa.shoulderAngle ?? 0,
              });

              // Color palette per case uiColor.
              const palette: Record<PartialGazeEstimate['uiColor'], string> = {
                green:  '#22c55e',
                yellow: '#eab308',
                orange: '#f97316',
                red:    '#ef4444',
              };
              const color = palette[estimate.uiColor];

              overlayCtx.save();
              overlayCtx.translate(cw, 0);
              overlayCtx.scale(-1, 1);

              // ray rendering (only when confidence allows it)
              if (estimate.drawRay && estimate.origin && estimate.yawDeg !== null) {
                const ox = estimate.origin.x * cw;
                const oy = estimate.origin.y * ch;
                const yawRad = (estimate.yawDeg * Math.PI) / 180;
                const pitchRad = ((estimate.pitchDeg ?? 0) * Math.PI) / 180;
                const projLen = ch * 0.6;
                const tgtX = clamp(ox + Math.sin(yawRad) * projLen * 0.5, cw * 0.05, cw * 0.95);
                const tgtY = clamp(oy - Math.sin(pitchRad) * projLen, ch * 0.05, ch * 0.95);

                // Confidence-modulated alpha + line dash for low confidence.
                const rayAlpha = estimate.confidence === 'high' ? 0.85
                  : estimate.confidence === 'med' ? 0.6
                  : 0.4;
                const dash: number[] = estimate.confidence === 'low' ? [6, 6] : [];

                overlayCtx.strokeStyle = color;
                overlayCtx.lineWidth = estimate.confidence === 'high' ? 3 : 2;
                overlayCtx.globalAlpha = rayAlpha;
                overlayCtx.setLineDash(dash);
                overlayCtx.beginPath();
                overlayCtx.moveTo(ox, oy);
                overlayCtx.lineTo(tgtX, tgtY);
                overlayCtx.stroke();
                overlayCtx.setLineDash([]);
                overlayCtx.globalAlpha = 1;
              }

              overlayCtx.restore();

              // Badge: "<icon> narratorText" + 신뢰도 표시 — 미러 X
              overlayCtx.save();
              const confLabel: Record<PartialGazeEstimate['confidence'], string> = {
                high:    '정밀',
                med:     '보통',
                low:     '낮음',
                unknown: '추적불가',
              };
              const confText = confLabel[estimate.confidence];
              const badgeText = `${estimate.narratorText} · ${confText}`;
              overlayCtx.font = '700 13px Pretendard, sans-serif';
              overlayCtx.textAlign = 'right';
              const tm = overlayCtx.measureText(badgeText);
              overlayCtx.fillStyle = 'rgba(0,0,0,0.7)';
              overlayCtx.beginPath();
              overlayCtx.roundRect(cw - tm.width - 18, 2, tm.width + 16, 24, 6);
              overlayCtx.fill();
              overlayCtx.strokeStyle = color;
              overlayCtx.lineWidth = 2;
              overlayCtx.stroke();
              overlayCtx.fillStyle = color;
              overlayCtx.fillText(badgeText, cw - 10, 18);

              overlayCtx.restore();

              // Face lost: reset gaze zone to neutral 'forward' (no inside/outside
              // claim — partial-visibility narratorText already conveys uncertainty).
              setGazeZone('forward');
            }
            return;
          }

          setHasFace(true);
          const landmarks = result.faceLandmarks[0];
          landmarksRef.current = landmarks;

          // Compute & expose face bbox (with 5% margin) so useHandLandmarker
          // can reject hand-on-face false positives.
          if (faceBboxRef && landmarks.length > 0) {
            let minX = 1, maxX = 0, minY = 1, maxY = 0;
            for (const p of landmarks) {
              if (p.x < minX) minX = p.x;
              if (p.x > maxX) maxX = p.x;
              if (p.y < minY) minY = p.y;
              if (p.y > maxY) maxY = p.y;
            }
            const mx = (maxX - minX) * 0.05;
            const my = (maxY - minY) * 0.05;
            faceBboxRef.current = {
              minX: Math.max(0, minX - mx),
              maxX: Math.min(1, maxX + mx),
              minY: Math.max(0, minY - my),
              maxY: Math.min(1, maxY + my),
            };
          }

          // ── Mirrored overlay context for face mesh + object bboxes ──
          // Face mesh is visually noisy on a real-subject demo recording.
          // Default OFF — enable with `?faceMesh=1` for debugging.
          const SHOW_FACE_MESH = typeof window !== 'undefined'
            && window.location.search.includes('faceMesh=1');
          if (drawUtilsRef.current && overlayCtx && overlayCanvas) {
            overlayCtx.save();
            // Mirror the canvas to match the mirrored video
            overlayCtx.translate(overlayCanvas.width, 0);
            overlayCtx.scale(-1, 1);

            if (SHOW_FACE_MESH) {
              drawUtilsRef.current.drawConnectors(
                landmarks, FaceLandmarker.FACE_LANDMARKS_TESSELATION,
                { color: '#5FA8FF60', lineWidth: 1 }
              );
              drawUtilsRef.current.drawConnectors(
                landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE,
                { color: '#3B9AFF', lineWidth: 2 }
              );
              drawUtilsRef.current.drawConnectors(
                landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE,
                { color: '#3B9AFF', lineWidth: 2 }
              );
              drawUtilsRef.current.drawConnectors(
                landmarks, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL,
                { color: '#3B9AFF90', lineWidth: 2 }
              );

              // Draw iris dots
              if (landmarks.length > 473) {
                [468, 473].forEach(idx => {
                  const lm = landmarks[idx];
                  overlayCtx!.beginPath();
                  overlayCtx!.arc(
                    lm.x * overlayCanvas.width,
                    lm.y * overlayCanvas.height,
                    5, 0, 2 * Math.PI
                  );
                  overlayCtx!.fillStyle = '#3B9AFF';
                  overlayCtx!.fill();
                  overlayCtx!.strokeStyle = '#fff';
                  overlayCtx!.lineWidth = 1.5;
                  overlayCtx!.stroke();
                });
              }
            }

            // ── Draw bbox + label for every detected object ──
            // All detections get a box with the label centered above the
            // box. The ray-snap highlight (if active in main view) still
            // overlays this pass with a thicker border.
            const allDets = detectionsRef?.current ?? [];
            for (const det of allDets) {
              // Whitelist — only 'cell phone' is drawn. Every other COCO
              // class misfires in this demo setup (pen → 'scissors',
              // paper → 'laptop'/'remote'). See LiveDemoApp comment.
              if (det.label !== 'cell phone') continue;
              if (det.score < 0.35) continue;
              const bb = det.bboxNorm;
              const cw0 = overlayCanvas.width;
              const ch0 = overlayCanvas.height;
              const bx = bb.x * cw0;
              const by = bb.y * ch0;
              const bw = bb.w * cw0;
              const bh = bb.h * ch0;
              const boxColor =
                det.salience === 'high' ? '#ef4444'
                : det.salience === 'low'  ? '#eab308'
                : '#06b6d4';

              overlayCtx!.strokeStyle = boxColor;
              overlayCtx!.lineWidth = 2;
              overlayCtx!.globalAlpha = 0.85;
              overlayCtx!.strokeRect(bx, by, bw, bh);
              overlayCtx!.globalAlpha = 1;

              // Label pill centered above the bbox. Counter-mirror so
              // text reads left-to-right instead of flipping.
              const labelName = IS_RECORDING_MODE ? det.label : det.labelKo;
              const labelText = `${labelName} ${Math.round(det.score * 100)}%`;
              overlayCtx!.save();
              overlayCtx!.translate(bx + bw / 2, Math.max(by - 6, 12));
              overlayCtx!.scale(-1, 1);
              overlayCtx!.font = '700 12px Pretendard, sans-serif';
              overlayCtx!.textAlign = 'center';
              const tw = overlayCtx!.measureText(labelText).width;
              const pillW = tw + 12;
              overlayCtx!.fillStyle = boxColor;
              overlayCtx!.beginPath();
              overlayCtx!.roundRect(-pillW / 2, -16, pillW, 18, 4);
              overlayCtx!.fill();
              overlayCtx!.fillStyle = '#fff';
              overlayCtx!.fillText(labelText, 0, -3);
              overlayCtx!.restore();
            }

            overlayCtx.restore();
          }

          // ── Gaze zone: face-plane normal projection from landmarks ──
          // Uses nose-tip vs face-center vector (NOT transformation matrix
          // Euler angles) for projection direction. This is directly visible
          // in the face mesh, so the projection direction exactly matches
          // what the user sees. FOV width from inter-eye distance.
          const drawGazeProjection = (yawDeg: number, pitchDeg: number) => {
            if (!overlayCtx || !overlayCanvas) return;
            if (!landmarks || landmarks.length < 474) return;

            const zone = classifyGazeZone(yawDeg, pitchDeg);
            setGazeZone(zone);
            const meta = ZONE_META[zone];

            const cw = overlayCanvas.width;
            const ch = overlayCanvas.height;

            overlayCtx.save();
            overlayCtx.translate(cw, 0);
            overlayCtx.scale(-1, 1);

            // ── Face direction from landmarks ──
            // Face center = midpoint(forehead 10, chin 152)
            // Nose tip (1) protrudes in the direction the face points.
            // The vector nose − faceCenter in image coords directly gives
            // the projection direction with NO sign/mirror ambiguity.
            const fhd = landmarks[10];
            const chin = landmarks[152];
            const nose = landmarks[1];
            setFaceNormY(nose.y);
            const fcx = (fhd.x + chin.x) / 2;
            const fcy = (fhd.y + chin.y) / 2;
            const fdx = nose.x - fcx; // positive = face pointing right in image
            const fdy = nose.y - fcy; // positive = face pointing down in image
            const fdMag = Math.sqrt(fdx * fdx + fdy * fdy);
            const ndx = fdMag > 0.001 ? fdx / fdMag : 0;
            const ndy = fdMag > 0.001 ? fdy / fdMag : 0.5;

            // ── Eye landmarks + inter-eye distance ──
            const lIris = landmarks[468];
            const rIris = landmarks[473];
            const lx = lIris.x * cw, ly = lIris.y * ch;
            const rx = rIris.x * cw, ry = rIris.y * ch;
            const eyeMidX = (lx + rx) / 2;
            const eyeMidY = (ly + ry) / 2;
            const interEye = Math.sqrt((rx - lx) ** 2 + (ry - ly) ** 2);

            // ── Object snapping: find the detected object closest to gaze ──
            // If an object is near the gaze direction, snap the projection
            // ellipse to that object's bounding box. Otherwise free-project.
            const dets = detectionsRef?.current ?? [];
            let snappedDet: Detection | null = null;
            let snapCx = 0, snapCy = 0, snapRx = 0, snapRy = 0;

            if (dets.length > 0 && fdMag > 0.005) {
              // Face center in normalized coords (to exclude "self" detections)
              const faceCxNorm = (fhd.x + chin.x) / 2;
              const faceCyNorm = (fhd.y + chin.y) / 2;

              const gazeAngle = Math.atan2(ndy, ndx);
              let bestScore = Infinity;

              for (const det of dets) {
                const bb = det.bboxNorm;

                // Skip detections where the face center is INSIDE the bbox
                // → this is almost certainly the student themselves (especially
                // "person" class which covers the upper body including the face).
                const faceInside =
                  faceCxNorm >= bb.x && faceCxNorm <= bb.x + bb.w &&
                  faceCyNorm >= bb.y && faceCyNorm <= bb.y + bb.h;
                if (faceInside) continue;

                const ocx = (bb.x + bb.w / 2) * cw;
                const ocy = (bb.y + bb.h / 2) * ch;
                const objAngle = Math.atan2(ocy - eyeMidY, ocx - eyeMidX);
                let angleDiff = Math.abs(gazeAngle - objAngle);
                if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
                const dist = Math.sqrt((ocx - eyeMidX) ** 2 + (ocy - eyeMidY) ** 2) / ch;
                const score = angleDiff * 3 + dist;
                if (angleDiff < 50 * Math.PI / 180 && score < bestScore) {
                  bestScore = score;
                  snappedDet = det;
                  snapCx = ocx;
                  snapCy = ocy;
                  snapRx = (bb.w / 2) * cw + interEye * 0.5;
                  snapRy = (bb.h / 2) * ch + interEye * 0.3;
                }
              }
            }

            // ── Phone-grip redirect: if the hand is in a phone-holding
            //    pose, the gaze cone target snaps to the hand. Encodes
            //    the inference "gaze is likely going to whatever is in
            //    the hand" even when the phone itself is occluded.
            const gripAux = phoneGripRef?.current;
            const gripActive = !!(
              gripAux &&
              gripAux.grip.isGrip &&
              gripAux.grip.center &&
              gripAux.sustainFrames >= 3
            );

            // ── Projection target: phone-grip > snapped object > free ──
            let tx: number, ty: number, ellipseRx: number, ellipseRy: number;
            let offScreen = false;
            // Raw (unclamped) gaze target — used for paper region classification
            const projLen = ch * Math.max(0.5, clamp(fdMag * 28, 0.5, 1.5));
            const rawTx = eyeMidX + ndx * projLen;
            const rawTy = eyeMidY + ndy * projLen;

            if (gripActive) {
              const gc = gripAux!.grip.center!;
              tx = gc.x * cw;
              ty = gc.y * ch;
              ellipseRx = interEye * 1.2;
              ellipseRy = interEye * 1.0;
            } else if (snappedDet) {
              tx = snapCx;
              ty = snapCy;
              ellipseRx = snapRx;
              ellipseRy = snapRy;
            } else {
              // Free projection along face normal
              tx = clamp(rawTx, cw * 0.05, cw * 0.95);
              ty = clamp(rawTy, ch * 0.05, ch * 0.95);
              offScreen = Math.sqrt((rawTx - tx) ** 2 + (rawTy - ty) ** 2) / ch > 0.03;
              // Small fdMag (forward) → small ellipse; large fdMag (tilted) → bigger
              const depthFactor = 0.5 + fdMag * 18;
              ellipseRx = interEye * clamp(depthFactor, 0.4, 3);
              const tiltV = Math.abs(fdy) / (fdMag || 1);
              ellipseRy = ellipseRx * clamp(1 - tiltV * 0.45, 0.4, 1);
            }

            // ── Desk-edge reference line ──
            const deskEdgeY = ch * 0.78;
            overlayCtx.strokeStyle = 'rgba(255,255,255,0.2)';
            overlayCtx.lineWidth = 1;
            overlayCtx.setLineDash([8, 6]);
            overlayCtx.beginPath();
            overlayCtx.moveTo(0, deskEdgeY);
            overlayCtx.lineTo(cw, deskEdgeY);
            overlayCtx.stroke();
            overlayCtx.setLineDash([]);
            overlayCtx.font = '500 9px Pretendard, sans-serif';
            overlayCtx.fillStyle = 'rgba(255,255,255,0.35)';
            overlayCtx.textAlign = 'left';
            overlayCtx.fillText('▲ 책상 위', 4, deskEdgeY - 4);
            overlayCtx.fillText('▼ 책상 아래', 4, deskEdgeY + 11);

            if (HEADPOSE_RAY_SHOW_LEGACY_CONE) {
              // Phone-grip override: paint the cone red so reviewers see
              // at a glance that the gaze target was redirected by the
              // hand pose, not the head pose.
              const coneColor = gripActive ? '#ef4444' : meta.color;

              // ── Trapezoid: each eye → ellipse tangent edges ──
              const tLx = tx - ellipseRx;
              const tRx = tx + ellipseRx;

              // Trapezoid fill (bold)
              overlayCtx.beginPath();
              overlayCtx.moveTo(lx, ly);
              overlayCtx.lineTo(tLx, ty);
              overlayCtx.lineTo(tRx, ty);
              overlayCtx.lineTo(rx, ry);
              overlayCtx.closePath();
              overlayCtx.fillStyle = coneColor + '45';
              overlayCtx.fill();

              // Edge lines (thick, vivid)
              overlayCtx.strokeStyle = coneColor;
              overlayCtx.lineWidth = 3;
              overlayCtx.globalAlpha = 0.9;
              overlayCtx.beginPath();
              overlayCtx.moveTo(lx, ly);
              overlayCtx.lineTo(tLx, ty);
              overlayCtx.moveTo(rx, ry);
              overlayCtx.lineTo(tRx, ty);
              overlayCtx.stroke();
              overlayCtx.globalAlpha = 1;

              // ── Ellipse (projected cone base, bold) ──
              overlayCtx.beginPath();
              overlayCtx.ellipse(tx, ty, ellipseRx, ellipseRy, 0, 0, Math.PI * 2);
              const grad = overlayCtx.createRadialGradient(tx, ty, 0, tx, ty, ellipseRx);
              grad.addColorStop(0, coneColor + '90');
              grad.addColorStop(0.5, coneColor + '50');
              grad.addColorStop(1, coneColor + '18');
              overlayCtx.fillStyle = grad;
              overlayCtx.fill();
              overlayCtx.strokeStyle = coneColor;
              overlayCtx.lineWidth = 4;
              overlayCtx.globalAlpha = 0.95;
              overlayCtx.stroke();
              overlayCtx.globalAlpha = 1;

              // Phone-grip marker: a small red 📱 label near the target.
              if (gripActive) {
                overlayCtx.save();
                overlayCtx.translate(tx, ty + ellipseRy + 16);
                overlayCtx.scale(-1, 1);
                const tag = IS_RECORDING_MODE ? '📱 Phone grip' : '📱 폰 그립 포즈';
                overlayCtx.font = '700 13px Pretendard, sans-serif';
                overlayCtx.textAlign = 'center';
                const tw = overlayCtx.measureText(tag).width + 14;
                overlayCtx.fillStyle = '#ef4444';
                overlayCtx.beginPath();
                overlayCtx.roundRect(-tw / 2, -12, tw, 20, 5);
                overlayCtx.fill();
                overlayCtx.fillStyle = '#fff';
                overlayCtx.fillText(tag, 0, 3);
                overlayCtx.restore();
              }
            }

            // Ray hit-point (normalized image coords) shared with the
            // paper-zone classifier below. Filled in by the HEADPOSE_RAY
            // block so zone matches what the user sees drawn — the legacy
            // (nose−faceCenter) Y disagreed in sign with the cross-product
            // forward used by the drawn ray, producing inverted "on/off
            // paper" badges.
            let rayHitForZone: { x: number; y: number } | null = null;
            let rayOriginForZone: { x: number; y: number } | null = null;

            if (HEADPOSE_RAY_ENABLED) {
              const originNorm = { x: eyeMidX / cw, y: eyeMidY / ch };
              const pa = poseAuxRef?.current ?? null;
              // Face-forward direction = MediaPipe facial transformation
              // matrix output (computed earlier in detect() and smoothed
              // into smoothRef). The matrix decomposition is well-defined
              // and frame-invariant, so the sign conventions don't drift
              // between head poses — replaces the prior cross-product
              // approach that needed empirical fwd{X,Y} sign flips.
              //
              // smoothRef.current.yaw   — positive = user yaws to their
              //                           RIGHT (matrix decomp convention:
              //                           physYaw = -θ → yawD = +θ)
              // smoothRef.current.pitch — positive = user looks UP
              //
              // Sign conventions tested empirically; the URL search exposes
              // runtime overrides so we don't have to redeploy to flip an
              // axis. Defaults flip BOTH from the prior matrix-derived
              // formula, matching the 2026-05-20 user report.
              //   ?invertX=0 → fwdX = -cos*sin (canvas mirror compensated)
              //   ?invertY=0 → fwdY = -sin (image up = -y)
              const search = typeof window !== 'undefined' ? window.location.search : '';
              const xSign = search.includes('invertX=0') ? -1 : 1;
              const ySign = search.includes('invertY=0') ? -1 : 1;
              const yawRad   = (smoothRef.current.yaw   * Math.PI) / 180;
              const pitchRad = (smoothRef.current.pitch * Math.PI) / 180;
              const fwdX = xSign * Math.cos(pitchRad) * Math.sin(yawRad);
              const fwdY = ySign * Math.sin(pitchRad);
              const fwdMag = Math.hypot(fwdX, fwdY);
              // Threshold scales with sin(1°) ≈ 0.0175 → require at least
              // ~3° of tilt before we draw. Below that the projection is
              // dominated by smoothing noise.
              const RAY_MIN_MAG = 0.05;

              const ray = castHeadPoseRay(
                fwdX,
                fwdY,
                originNorm,
                paperHomographyRef?.current ?? null,
                pa
                  ? {
                      shoulderAngleDeg: pa.shoulderAngle,
                      poseYawDeg: pa.yaw,
                      posePitchDeg: pa.pitch,
                      poseValid: pa.valid,
                      noseTip: { x: nose.x, y: nose.y },
                      faceCenter: { x: fcx, y: fcy },
                    }
                  : {
                      noseTip: { x: nose.x, y: nose.y },
                      faceCenter: { x: fcx, y: fcy },
                    },
              );

              // Frontal gaze: the direction points ~at the camera, so its
              // image-plane projection collapses to ~0 length and a drawn ray
              // would vanish. Instead of disappearing (user report 2026-06-22),
              // draw a clear "정면 응시" marker at the gaze origin so the
              // visualization is always present when looking at the screen.
              if (fwdMag < RAY_MIN_MAG) {
                overlayCtx.beginPath();
                overlayCtx.arc(eyeMidX, eyeMidY, 13, 0, Math.PI * 2);
                overlayCtx.strokeStyle = meta.color;
                overlayCtx.globalAlpha = 0.9;
                overlayCtx.lineWidth = 3;
                overlayCtx.stroke();
                overlayCtx.beginPath();
                overlayCtx.arc(eyeMidX, eyeMidY, 4, 0, Math.PI * 2);
                overlayCtx.fillStyle = meta.color;
                overlayCtx.fill();
                overlayCtx.globalAlpha = 1;
                overlayCtx.restore();
                return;
              }

              // Share the ray's endpoints with the paper-zone block so the
              // zone classification is computed from the same direction the
              // user sees drawn. We pass BOTH origin and hit-image so the
              // classifier can sample the segment (a ray pointed AT the
              // paper passes through it then exits the image — endpoint
              // alone would always read 'outside' for through-paper rays).
              rayOriginForZone = ray.originUsed;
              rayHitForZone = ray.hitImage;

              // Snap to the first object the ray enters up to the image
              // boundary (tMax). Exclude 'person' class outright — it is
              // always the student themselves in a 1:1 proctoring setup
              // and would snap the ray short to the torso.
              const faceCxNorm = (fhd.x + chin.x) / 2;
              const faceCyNorm = (fhd.y + chin.y) / 2;
              // Whitelist snap target — only 'cell phone' is reliable on
              // EfficientDet-Lite0 in this setup. Other COCO classes
              // ('book', 'laptop', 'remote', 'scissors', etc.) misclassify
              // the actual paper sheet and pull a misleading red salience
              // highlight onto the paper (CEO 0522 17:59 feedback:
              // "노트북·리모컨 박싱이 마음에 안 든다").
              const snappable = dets.filter(
                (d) => d.label === 'cell phone' && d.score >= 0.35,
              );
              const rayHit = firstObjectAlongRay(
                ray.originUsed,
                ray.dirX,
                ray.dirY,
                ray.tMax,
                snappable,
                { x: faceCxNorm, y: faceCyNorm },
              );

              let hitX: number;
              let hitY: number;
              let hitDet: Detection | null = null;
              if (rayHit) {
                hitDet = rayHit.detection;
                const bb = hitDet.bboxNorm;
                hitX = (bb.x + bb.w / 2) * cw;
                hitY = (bb.y + bb.h / 2) * ch;
                snappedDet = hitDet;
              } else {
                hitX = ray.hitImage.x * cw;
                hitY = ray.hitImage.y * ch;
              }

              const rayColor =
                hitDet && hitDet.salience === 'high' ? '#ef4444' : meta.color;

              // ── Binocular gaze envelope (replaces single midpoint cone) ──
              // Per CEO 0520 + 0522 feedback: cone should originate from the
              // EYES, not a single midpoint. Two cones (one per eye) both
              // pointed at the gaze target. They converge near the hit point
              // and form a natural "stereo viewing envelope" instead of a
              // spurious triangle sprouting from the bridge of the nose.
              //
              // Each cone has a tighter half-angle (~10°) because the two
              // overlapping cones already provide visual width.
              const dxC = hitX - eyeMidX;
              const dyC = hitY - eyeMidY;
              const segLenC = Math.hypot(dxC, dyC);
              if (segLenC > 0.5) {
                const HALF_ANGLE_RAD = (10 * Math.PI) / 180;
                // Local non-null alias so the inner arrow function doesn't
                // fight TS narrowing on the closured `overlayCtx`.
                const ctxC = overlayCtx;
                const drawEyeCone = (apexX: number, apexY: number) => {
                  const d2x = hitX - apexX;
                  const d2y = hitY - apexY;
                  const seg = Math.hypot(d2x, d2y);
                  if (seg < 0.5) return;
                  const dirNX = d2x / seg;
                  const dirNY = d2y / seg;
                  const perpNX = -dirNY;
                  const perpNY = dirNX;
                  const halfW = seg * Math.tan(HALF_ANGLE_RAD);
                  const farLX = hitX + perpNX * halfW;
                  const farLY = hitY + perpNY * halfW;
                  const farRX = hitX - perpNX * halfW;
                  const farRY = hitY - perpNY * halfW;

                  ctxC.beginPath();
                  ctxC.moveTo(apexX, apexY);
                  ctxC.lineTo(farLX, farLY);
                  ctxC.lineTo(farRX, farRY);
                  ctxC.closePath();
                  ctxC.fillStyle = rayColor + '22';
                  ctxC.fill();

                  ctxC.strokeStyle = rayColor;
                  ctxC.globalAlpha = 0.7;
                  ctxC.lineWidth = 2;
                  ctxC.stroke();
                  ctxC.globalAlpha = 1;
                };

                drawEyeCone(lx, ly);
                drawEyeCone(rx, ry);

                // Light spine from each eye → hit point (hairline).
                overlayCtx.strokeStyle = rayColor;
                overlayCtx.globalAlpha = 0.4;
                overlayCtx.lineWidth = 1;
                overlayCtx.beginPath();
                overlayCtx.moveTo(lx, ly);
                overlayCtx.lineTo(hitX, hitY);
                overlayCtx.moveTo(rx, ry);
                overlayCtx.lineTo(hitX, hitY);
                overlayCtx.stroke();
                overlayCtx.globalAlpha = 1;
              }

              if (hitDet) {
                const bb = hitDet.bboxNorm;
                const bx = bb.x * cw;
                const by = bb.y * ch;
                const bw = bb.w * cw;
                const bh = bb.h * ch;

                // Filled highlight — semi-transparent fill so the
                // object inside remains visible.
                overlayCtx.fillStyle = rayColor + '33';
                overlayCtx.fillRect(bx, by, bw, bh);

                // Outer glow ring — thick + translucent for a subtle
                // pulse effect at the periphery.
                overlayCtx.strokeStyle = rayColor;
                overlayCtx.lineWidth = 6;
                overlayCtx.globalAlpha = 0.35;
                overlayCtx.strokeRect(bx - 3, by - 3, bw + 6, bh + 6);

                // Inner solid border — crisp outline on the bbox.
                overlayCtx.lineWidth = 3;
                overlayCtx.globalAlpha = 1;
                overlayCtx.strokeRect(bx, by, bw, bh);

                // Corner ticks (L-brackets) to draw the eye to the box.
                const cornerLen = Math.min(18, bw * 0.25, bh * 0.25);
                overlayCtx.lineWidth = 4;
                overlayCtx.beginPath();
                // top-left
                overlayCtx.moveTo(bx, by + cornerLen); overlayCtx.lineTo(bx, by); overlayCtx.lineTo(bx + cornerLen, by);
                // top-right
                overlayCtx.moveTo(bx + bw - cornerLen, by); overlayCtx.lineTo(bx + bw, by); overlayCtx.lineTo(bx + bw, by + cornerLen);
                // bottom-right
                overlayCtx.moveTo(bx + bw, by + bh - cornerLen); overlayCtx.lineTo(bx + bw, by + bh); overlayCtx.lineTo(bx + bw - cornerLen, by + bh);
                // bottom-left
                overlayCtx.moveTo(bx + cornerLen, by + bh); overlayCtx.lineTo(bx, by + bh); overlayCtx.lineTo(bx, by + bh - cornerLen);
                overlayCtx.stroke();

                // Label pill above the bbox showing the Korean object
                // name. Counter-mirror so the text reads left-to-right
                // instead of flipping with the canvas scale(-1,1).
                const labelText = hitDet.labelKo;
                overlayCtx.save();
                overlayCtx.translate(bx + bw / 2, Math.max(by - 10, 14));
                overlayCtx.scale(-1, 1);
                overlayCtx.font = '700 13px Pretendard, sans-serif';
                overlayCtx.textAlign = 'center';
                const tw = overlayCtx.measureText(labelText).width;
                const pillW = tw + 16;
                overlayCtx.fillStyle = rayColor;
                overlayCtx.beginPath();
                overlayCtx.roundRect(-pillW / 2, -14, pillW, 22, 11);
                overlayCtx.fill();
                overlayCtx.fillStyle = '#fff';
                overlayCtx.fillText(labelText, 0, 2);
                overlayCtx.restore();
              }

              overlayCtx.beginPath();
              overlayCtx.arc(hitX, hitY, 7, 0, 2 * Math.PI);
              overlayCtx.fillStyle = rayColor;
              overlayCtx.fill();
              overlayCtx.strokeStyle = '#fff';
              overlayCtx.lineWidth = 1.5;
              overlayCtx.stroke();

              // Confidence ring — radius scales with (1-confidence) so low
              // confidence is visually larger/fuzzier. Supports Explainable
              // AI by showing reviewers when signals are uncertain.
              const ringR = 10 + (1 - ray.confidence) * 18;
              overlayCtx.beginPath();
              overlayCtx.arc(hitX, hitY, ringR, 0, 2 * Math.PI);
              overlayCtx.strokeStyle = rayColor;
              overlayCtx.globalAlpha = 0.35 + 0.35 * ray.confidence;
              overlayCtx.lineWidth = 1.5;
              overlayCtx.stroke();
              overlayCtx.globalAlpha = 1;

              // Auxiliary-signal chip: pose agreement + shoulder tilt hint.
              const pa2 = poseAuxRef?.current ?? null;
              const chipParts: string[] = [];
              if (ray.agreement !== null) {
                const pct = Math.round(ray.agreement * 100);
                chipParts.push(`pose ${pct >= 70 ? '✓' : '⚠'} ${pct}%`);
              }
              if (pa2 && Math.abs(pa2.shoulderAngle) > 4) {
                chipParts.push(`어깨 ${pa2.shoulderAngle >= 0 ? '우' : '좌'}기울`);
              }
              chipParts.push(`conf ${Math.round(ray.confidence * 100)}%`);
              const chip = chipParts.join(' · ');
              overlayCtx.font = '600 11px Pretendard, sans-serif';
              overlayCtx.textAlign = 'left';
              const chipW = overlayCtx.measureText(chip).width + 12;
              const chipX = Math.min(hitX + 10, cw - chipW - 4);
              const chipY = Math.max(hitY - 20, 2);
              overlayCtx.fillStyle = 'rgba(0,0,0,0.6)';
              overlayCtx.beginPath();
              overlayCtx.roundRect(chipX, chipY, chipW, 18, 5);
              overlayCtx.fill();
              overlayCtx.strokeStyle = rayColor;
              overlayCtx.lineWidth = 1;
              overlayCtx.stroke();
              overlayCtx.fillStyle = '#fff';
              overlayCtx.fillText(chip, chipX + 6, chipY + 12);
            }

            // ── Per-frame marker detection + homography update ──
            const MARKER_DETECT_INTERVAL = 3; // run every N-th frame
            const MARKER_ROI_SIZE = 80;
            const MARKER_LOST_FALLBACK = 30; // frames (~1s) before fallback to calibration H
            if (paperCornersRef?.current && paperCornersRef.current.length === 4) {
              markerFrameCountRef.current++;
              if (markerFrameCountRef.current % MARKER_DETECT_INTERVAL === 0) {
                if (!markerCanvasRef.current) {
                  markerCanvasRef.current = document.createElement('canvas');
                }
                const vid = document.getElementById(videoElementId) as HTMLVideoElement | null;
                if (vid) {
                  // Per-corner detection — 3-of-4 still usable via affine fallback.
                  const partial = detectMarkersPartial(
                    vid, paperCornersRef.current, MARKER_ROI_SIZE, markerCanvasRef.current,
                  );
                  // Sanity gate — each detected corner must stay within a small
                  // neighborhood of its expected position. Without this, the
                  // ROI scan grabs spurious dark squares (text on clothing,
                  // shadows, etc.) and `paperCornersRef` gets corrupted to
                  // wild positions — visible in LiveDemo mode where the
                  // webcam contains NO real markers, only the synthetic
                  // paper PNG which is a separate CSS overlay.
                  const MAX_CORNER_DRIFT = 0.05;
                  const visibleIdx: number[] = [];
                  const merged: HPoint[] = paperCornersRef.current.map((c, i) => {
                    if (!partial[i]) return { x: c.x, y: c.y };
                    const dx = partial[i]!.x - c.x;
                    const dy = partial[i]!.y - c.y;
                    if (Math.hypot(dx, dy) > MAX_CORNER_DRIFT) return { x: c.x, y: c.y };
                    visibleIdx.push(i);
                    return { x: partial[i]!.x, y: partial[i]!.y };
                  });

                  if (visibleIdx.length >= 3) {
                    const ph = computePartialHomography(merged, PAPER_DST as unknown as HPoint[], visibleIdx);
                    if (ph.H && paperHomographyRef) {
                      paperHomographyRef.current = ph.H;
                      paperCornersRef.current = merged;
                      markerLostCountRef.current = 0;
                      latestHomographyKindRef.current = ph.kind === 'full' ? 'full' : 'affine';
                      latestHomographyConfRef.current = ph.confidence;
                    }
                  } else {
                    markerLostCountRef.current++;
                    if (markerLostCountRef.current > MARKER_LOST_FALLBACK
                        && calibHomographyRef?.current && calibCornersRef?.current) {
                      if (paperHomographyRef) paperHomographyRef.current = calibHomographyRef.current;
                      paperCornersRef.current = calibCornersRef.current;
                      latestHomographyKindRef.current = 'calibration';
                      latestHomographyConfRef.current = 0.5;
                    } else if (markerLostCountRef.current > MARKER_LOST_FALLBACK) {
                      latestHomographyKindRef.current = 'none';
                      latestHomographyConfRef.current = 0;
                    }
                  }
                }
              }
            }

            // ── Paper outline + region (if homography available) ──
            const H = paperHomographyRef?.current;
            const pCorners = paperCornersRef?.current;
            let paperRegionLabel = '';
            if (H && pCorners && pCorners.length === 4) {
              // Draw paper outline on overlay — color reflects PREVIOUS
              // frame's zone (we update zone state below).
              const prevZone = paperZoneStateRef?.current?.zone;
              const outlineColor =
                prevZone === 'confident_inside' ? '#22c55e'
                : prevZone === 'boundary' ? '#eab308'
                : prevZone === 'confident_outside' ? '#ef4444'
                : '#5ED28F';
              overlayCtx.beginPath();
              for (let pi = 0; pi < 4; pi++) {
                const px = pCorners[pi].x * cw;
                const py = pCorners[pi].y * ch;
                if (pi === 0) overlayCtx.moveTo(px, py);
                else overlayCtx.lineTo(px, py);
              }
              overlayCtx.closePath();
              overlayCtx.strokeStyle = outlineColor;
              overlayCtx.lineWidth = prevZone === 'confident_inside' ? 4 : 3;
              overlayCtx.globalAlpha = 0.85;
              // Affine fallback (3 corners) — show as longer dash to signal
              // reduced confidence to the operator; calibration fallback uses
              // tight dotted style.
              const hk = latestHomographyKindRef.current;
              if (hk === 'affine')        overlayCtx.setLineDash([16, 8]);
              else if (hk === 'calibration') overlayCtx.setLineDash([3, 6]);
              else                         overlayCtx.setLineDash([10, 5]);
              overlayCtx.stroke();
              overlayCtx.setLineDash([]);
              overlayCtx.globalAlpha = 1;

              // Tiny corner badge — '3-pt' for affine, 'CAL' for calibration.
              if (hk === 'affine' || hk === 'calibration') {
                const badgeText = hk === 'affine' ? '3-pt 추정' : 'CAL 고정';
                overlayCtx.save();
                overlayCtx.font = '700 11px Pretendard, sans-serif';
                overlayCtx.textAlign = 'left';
                const bx = pCorners[0].x * cw + 6;
                const by = pCorners[0].y * ch - 6;
                const tm = overlayCtx.measureText(badgeText);
                overlayCtx.fillStyle = 'rgba(0,0,0,0.75)';
                overlayCtx.fillRect(bx - 2, by - 12, tm.width + 8, 14);
                overlayCtx.fillStyle = hk === 'affine' ? '#eab308' : '#9ca3af';
                overlayCtx.fillText(badgeText, bx + 2, by - 1);
                overlayCtx.restore();
              }

              // Map gaze target to paper coordinates — use raw (unclamped)
              // coordinates so off-screen gaze correctly classifies as 'outside'.
              // 3-zone classification + duration filter.
              let rawZone: PaperZone = 'confident_outside';
              let pRegion: PaperRegion | null = null;
              if (offScreen && rayHitForZone === null) {
                rawZone = 'confident_outside';
              } else {
                const margin = paperZoneMarginRef?.current ?? PAPER_ZONE_MARGIN;
                // Sample the ray segment in paper coords. A ray pointed AT
                // the paper passes through it before exiting the image, so
                // we need to look at multiple t-values, not just the
                // endpoint. Pick the best zone encountered along the ray —
                // confident_inside > boundary > confident_outside.
                let bestZone: PaperZone = 'confident_outside';
                let bestRegion: PaperRegion | null = null;
                if (rayOriginForZone && rayHitForZone) {
                  const SAMPLES = 12;
                  for (let i = 1; i <= SAMPLES; i++) {
                    const t = i / SAMPLES;
                    const ix = rayOriginForZone.x + t * (rayHitForZone.x - rayOriginForZone.x);
                    const iy = rayOriginForZone.y + t * (rayHitForZone.y - rayOriginForZone.y);
                    const p = applyHomography(H, { x: ix, y: iy });
                    const z = classifyPaperZone(p.x, p.y, margin);
                    if (z === 'confident_inside') {
                      bestZone = z;
                      bestRegion = classifyPaperRegion(p.x, p.y);
                      break;
                    } else if (z === 'boundary') {
                      // Keep first boundary; only overwrite on confident_inside above.
                      if (bestZone === 'confident_outside') {
                        bestZone = z;
                        bestRegion = classifyPaperRegion(p.x, p.y);
                      }
                    }
                  }
                } else {
                  const zoneInput = { x: rawTx / cw, y: rawTy / ch };
                  const p = applyHomography(H, zoneInput);
                  bestZone = classifyPaperZone(p.x, p.y, margin);
                  bestRegion = classifyPaperRegion(p.x, p.y);
                }
                rawZone = bestZone;
                pRegion = bestRegion;
                if (rawZone === 'confident_inside' && pRegion) {
                  paperRegionLabel = ` · ${PAPER_REGION_LABELS[pRegion]}`;
                }
              }
              const filteredZone = paperGazeFilterRef.current.update(rawZone);
              const zoneMeta = PAPER_ZONE_META[filteredZone];
              if (!paperRegionLabel || filteredZone !== 'confident_inside') {
                paperRegionLabel = ` · ${zoneMeta.label}`;
              }
              if (paperZoneStateRef) {
                paperZoneStateRef.current = {
                  zone: filteredZone,
                  region: pRegion,
                  offScreen,
                  t: performance.now(),
                  homographyKind: latestHomographyKindRef.current,
                  homographyConfidence: latestHomographyConfRef.current,
                };
              }
            }

            // ── Zone badge + snapped object + paper region ──
            // Drawn OUTSIDE the mirror transform so text is readable.
            // In IS_RECORDING_MODE we use English labels for international
            // demo clarity.
            overlayCtx.restore();
            overlayCtx.save();

            const zoneLabel = IS_RECORDING_MODE ? meta.labelEn : meta.label;
            const snapName = snappedDet
              ? (IS_RECORDING_MODE ? snappedDet.label : snappedDet.labelKo)
              : '';
            const snapLabel = snappedDet ? ` → ${snapName}` : '';
            const offLabel = offScreen
              ? (IS_RECORDING_MODE ? ' (off-screen)' : ' (화면 밖)')
              : '';
            const bText = `${meta.icon} ${zoneLabel}${snapLabel}${paperRegionLabel}${offLabel}`;
            const badgeColor = snappedDet && snappedDet.salience === 'high'
              ? '#ef4444' : meta.color;
            overlayCtx.font = '700 14px Pretendard, sans-serif';
            overlayCtx.textAlign = 'left';
            const tm2 = overlayCtx.measureText(bText);
            const bw2 = tm2.width + 16, bh2 = 26;
            const bx2 = 10, by2 = 14;
            overlayCtx.fillStyle = 'rgba(0,0,0,0.6)';
            overlayCtx.beginPath();
            overlayCtx.roundRect(bx2, by2 - bh2 / 2 - 1, bw2, bh2, 6);
            overlayCtx.fill();
            overlayCtx.strokeStyle = badgeColor;
            overlayCtx.lineWidth = 2;
            overlayCtx.stroke();
            overlayCtx.fillStyle = badgeColor;
            overlayCtx.fillText(bText, bx2 + 8, by2 + 4);

            overlayCtx.restore();
          };

          // ── Head pose from transformation matrix ──
          if (result.facialTransformationMatrixes?.length > 0) {
            const m = result.facialTransformationMatrixes[0].data;
            const r00 = m[0], r10 = m[4], r20 = m[8], r21 = m[9], r22 = m[10];
            const DEG = 180 / Math.PI;

            const physYaw = Math.asin(-clamp(r20, -1, 1));
            const physPitch = Math.atan2(r21, r22);
            const physRoll = Math.atan2(r10, r00);

            const yawD = -physYaw * DEG;
            const pitchD = -physPitch * DEG;  // 부호 반전: 위를 보면 양수
            const rollD = -physRoll * DEG;

            smoothRef.current.yaw += EMA_ALPHA * (yawD - smoothRef.current.yaw);
            smoothRef.current.pitch += EMA_ALPHA * (pitchD - smoothRef.current.pitch);
            smoothRef.current.roll += EMA_ALPHA * (rollD - smoothRef.current.roll);

            // ── Optional distilled ONNX head-pose override (?headPoseOnnx=on) ──
            // Crops the head from the current frame (landmark extent → head box,
            // matching training framing) and overrides yaw/pitch with the 0.32MB
            // student. Single-flight async; uses the freshest result each frame.
            if (HEADPOSE_ONNX && landmarks && landmarks.length > 0) {
              let minX = 1, minY = 1, maxX = 0, maxY = 0;
              for (const p of landmarks) {
                if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
                if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
              }
              const side = Math.max(maxX - minX, maxY - minY) * 1.6;
              const bcx = (minX + maxX) / 2;
              const bcy = (minY + maxY) / 2 - side * 0.08;  // nudge up for forehead/hair
              const bbox = { x: bcx - side / 2, y: bcy - side / 2, w: side, h: side };
              const vidEl = document.getElementById(videoElementId) as HTMLVideoElement | null;
              if (vidEl) {
                if (!hpCanvasRef.current) hpCanvasRef.current = document.createElement('canvas');
                void detectHeadPose(vidEl, bbox, hpCanvasRef.current);
                const hp = getHeadPoseSync();
                if (hp) {
                  smoothRef.current.yaw = (HP_FLIP_YAW ? -1 : 1) * hp.yaw;
                  smoothRef.current.pitch = (HP_FLIP_PITCH ? -1 : 1) * hp.pitch;
                }
              }
            }

            // Head vector
            const hx = Math.cos(-physPitch) * Math.sin(physYaw);
            const hy = Math.sin(-physPitch);
            const hz = -Math.cos(-physPitch) * Math.cos(physYaw);

            // ── Iris-based gaze estimation ──
            // Uses iris position relative to eye corners + head pose contribution
            let normPogX = 0;
            let normPogY = 0;

            if (landmarks.length > 473) {
              // Left eye: iris=468, outer=33, inner=133
              // Right eye: iris=473, outer=362, inner=263
              // NOTE: MediaPipe landmarks are in original (non-mirrored) coordinates
              const lIris = landmarks[468], lOuter = landmarks[33], lInner = landmarks[133];
              const rIris = landmarks[473], rOuter = landmarks[362], rInner = landmarks[263];

              // Horizontal iris ratio: 0 = looking at outer corner, 1 = looking at inner corner
              const lEyeWidth = lInner.x - lOuter.x;
              const rEyeWidth = rOuter.x - rInner.x;

              // Left eye ratio (normalized 0-1)
              const lRatioX = lEyeWidth !== 0 ? (lIris.x - lOuter.x) / lEyeWidth : 0.5;
              // Right eye ratio (normalized 0-1, same direction)
              const rRatioX = rEyeWidth !== 0 ? (rIris.x - rInner.x) / rEyeWidth : 0.5;

              // Average iris horizontal position (0=looking left, 0.5=center, 1=looking right)
              const avgIrisX = (lRatioX + rRatioX) / 2;

              // Vertical iris ratio
              const lEyeHeight = Math.abs(landmarks[159].y - landmarks[145].y); // Upper-lower eyelid
              const rEyeHeight = Math.abs(landmarks[386].y - landmarks[374].y);
              const lCenterY = (landmarks[159].y + landmarks[145].y) / 2;
              const rCenterY = (landmarks[386].y + landmarks[374].y) / 2;
              const lRatioY = lEyeHeight > 0.001 ? (lIris.y - lCenterY) / lEyeHeight : 0;
              const rRatioY = rEyeHeight > 0.001 ? (rIris.y - rCenterY) / rEyeHeight : 0;
              const avgIrisY = (lRatioY + rRatioY) / 2;

              // Map iris ratio to screen coordinates
              // Iris contributes ~60% of gaze, head pose contributes ~40%
              const irisGainX = 1.5;  // Sensitivity multiplier
              const irisGainY = 2.0;
              const headGainX = 0.015; // degrees to normalized coords
              const headGainY = 0.012;

              const irisX = (avgIrisX - 0.5) * irisGainX;
              const irisY = avgIrisY * irisGainY;

              const headX = smoothRef.current.yaw * headGainX;
              const headY = -smoothRef.current.pitch * headGainY;

              const rawX = irisX + headX;
              const rawY = irisY + headY;

              // EMA smoothing on gaze point
              gazeSmooth.current.x += 0.4 * (rawX - gazeSmooth.current.x);
              gazeSmooth.current.y += 0.4 * (rawY - gazeSmooth.current.y);

              normPogX = gazeSmooth.current.x;
              normPogY = gazeSmooth.current.y;
            }

            setGazeData({
              normPog: [
                clamp(normPogX, -0.5, 0.5),
                clamp(normPogY, -0.5, 0.5),
              ] as [number, number],
              headVector: [hx, hy, hz] as [number, number, number],
              gazeState: 'open',
              timestamp: Date.now(),
              yaw: smoothRef.current.yaw,
              pitch: smoothRef.current.pitch,
            });

            // Draw gaze projection lines AFTER head pose is computed
            drawGazeProjection(smoothRef.current.yaw, smoothRef.current.pitch);
          }
        } catch {
          // Ignore detection errors
        }
      };

      animRef.current = requestAnimationFrame(detect);
    };

    init();

    return () => {
      disposed = true;
      cancelAnimationFrame(animRef.current);
    };
  }, [videoElementId, overlayCanvasId]);

  return { gazeData, hasFace, fps, roll: smoothRef.current.roll, landmarksRef, gazeZone, faceNormY };
}
